import clsx from "clsx";
import { Helmet } from "react-helmet";

const TextBlock = ({ children, className }) => (
  <p className={clsx("w-full", className)}>{children}</p>
);

const SubHeader = ({ children }) => (
  <h2 className="text-gold text-2xl my-6 text-center">{children}</h2>
);

const AdvancedStats = () => {
  return (
    <section className="section about-section container">
      <Helmet>
        <title>About</title>
      </Helmet>

      <header className="text-center px-4">
        <h1 className="text-gold text-3xl">A Fresh Perspective</h1>
      </header>

      <div className="flex flex-col items-start justify-start">
        <TextBlock className="text-center text-black-100">
          Drives markets deliver powerful insights that traditional stats miss.
          Track momentum shifts, possession efficiency, and scoring patterns as
          they unfold in real-time.
        </TextBlock>
        <TextBlock className="text-white">
          <strong className="text-gold">Drives Score (DS)</strong> — Represents
          the situational value (i.e., down/distance) in game scoring terms –
          it’s the foundation for our suite of advanced metrics.
        </TextBlock>
        <TextBlock className="text-white">
          <strong className="text-gold">Drives Score Added (DSA)</strong> —
          Measures how much each play changes the offense’s scoring prospects.
          Puts value to every facet of the game – takeaways, sacks, penalties,
          and more.
        </TextBlock>
        <TextBlock className="text-white">
          <strong className="text-gold">Clutch Score (CS)</strong> — The{" "}
          <strong>Gamechanger</strong>, adjusts DSA to highlight pivotal plays.
          Identifies each week’s <strong>Heroes & Zeros</strong>.
        </TextBlock>
        <TextBlock className="text-white">
          <strong className="text-gold">Drives Impact Score (DIS)</strong> — The{" "}
          <strong>Garbage Meter</strong>; boosts meaningful plays to separate
          them from "garbage time" stat padding.
        </TextBlock>
        <TextBlock className="text-white">
          <strong className="text-gold">Drives Impact Ratio (DIR)</strong> — A{" "}
          <strong>Blowout Rating</strong>; measures a team’s performance during
          competitive play vs blowouts.
        </TextBlock>
      </div>

      <SubHeader>Pre-Game Analysis</SubHeader>
      <div className="flex flex-col items-center space-y-4">
        <TextBlock className="text-center w-full flex justify-center">
          Dissects team strengths, player matchups, and situational advantages
          for a winning edge.
        </TextBlock>
        {[
          "Reality Check – are the box score stats “real”?",
          "Scoring DNA – what’s really driving scoring?",
          "Matchup Scouting – ignore the chatter, find the advantages oddsmakers miss.",
          "Regression Alerts – are seasonal strengths sustainable?",
          "Fluke Factor – has a team been good or just lucky?",
          "Winning Mentality – does a team know how to win or just lose in style?",
          "Breakouts – identify opportunities for best bets or parlays.",
        ].map((text, i) => (
          <TextBlock key={i} className="text-white">
            <strong className="text-gold">{text.split("–")[0]}</strong> –{" "}
            {text.split("–")[1]}
          </TextBlock>
        ))}
      </div>

      <div>
        <SubHeader>In-Game</SubHeader>
        <TextBlock className="text-center w-full flex justify-center">
          Compare season trends with game action to identify in-game and prop
          opportunities.
        </TextBlock>
      </div>

      <SubHeader>Example Wagers</SubHeader>
      <div className="flex flex-col items-center">
        {[
          {
            title: "Garbage Meter",
            desc: `With a 72% Garbage Meter rating, traditional stats overestimate the NY Jets’ offense. Take Miami -3.`,
          },
          {
            title: "Blowout Rating",
            desc: `Detroit’s defense has an 83% Blowout Rating, meaning opponents rack up meaningless stats. Take the Under.`,
          },
          {
            title: "Reality Check",
            desc: `Josh Allen’s low interception DSA shows he’s been aggressive, not reckless. Take Bills -5.`,
          },
          {
            title: "Matchup Scouting",
            desc: `Cincy’s deep threats will benefit from Raiders’ penalty-prone secondary. Take Cincinnati -8.`,
          },
          {
            title: "Regression Alerts",
            desc: `Exceptional field position boosted Rams DSA; expect regression. Take Bucs +4.`,
          },
          {
            title: "In-Game",
            desc: `The Patriots often trail at half, but Maye mounts late comebacks. Take Pats +11.`,
          },
        ].map(({ title, desc }, i) => (
          <TextBlock key={i}>
            <strong className="text-gold">{title}:</strong>{" "}
            <span className="text-white">{desc}</span>
          </TextBlock>
        ))}
        <TextBlock>
          <p className="mt-32 text-lg lg:text-3xl text-center font-bold text-gold">
            This is just a glimpse of our analytical edge. Drives is committed
            to developing cutting-edge metrics in collaboration with content and
            gaming partners.
          </p>
        </TextBlock>
      </div>
    </section>
  );
};

export default AdvancedStats;
